import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { MODULE_SUMMARY_TABLE_SVGS, TOC_MODULE_COLORS } from "./constants.js";
import chapterBubbleSvg from "../assets/print/chapter-bubble.svg.js";
import { PrintDotLine } from "./PrintDotLine.js";
import { ContentMetadataText } from "../components/common/ContentMetadataText.js";
const SVG_W = 650;
const SVG_H = 672;
const LESSON_LIST_MIN_WIDTH = 360;
function ChapterBubble({
  number,
  accentColor
}) {
  return /* @__PURE__ */ jsxs("div", { style: { position: "relative", width: 374, height: 247 }, children: [
    /* @__PURE__ */ jsx(
      "img",
      {
        src: chapterBubbleSvg,
        alt: "",
        style: {
          width: "100%",
          height: "100%",
          position: "absolute",
          top: 0,
          left: 0,
          pointerEvents: "none"
        }
      }
    ),
    /* @__PURE__ */ jsx(
      "span",
      {
        "data-testid": "chapter-number",
        className: "font-dunggeunmiso",
        style: {
          position: "absolute",
          left: "50%",
          top: `${122.635 / 248 * 100}%`,
          transform: "translate(-50%, -50%)",
          fontSize: 110,
          fontWeight: 700,
          color: accentColor,
          lineHeight: "normal"
        },
        children: number
      }
    )
  ] });
}
function LessonListItem({
  number,
  lessonName,
  accentColor
}) {
  const numStr = String(number).padStart(2, "0");
  return /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: 10, fontSize: 18, lineHeight: "26px" }, children: [
    /* @__PURE__ */ jsxs(
      "div",
      {
        style: {
          display: "flex",
          alignItems: "center",
          height: 26,
          fontFamily: "'Pretendard', sans-serif",
          fontWeight: 700,
          color: accentColor,
          textAlign: "center",
          flexShrink: 0
        },
        children: [
          /* @__PURE__ */ jsx("span", { style: { width: 12, display: "inline-block", textAlign: "center" }, children: numStr[0] }),
          /* @__PURE__ */ jsx("span", { style: { width: 12, display: "inline-block", textAlign: "center" }, children: numStr[1] })
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      "span",
      {
        style: {
          fontFamily: "'Pretendard', sans-serif",
          fontWeight: 400,
          color: "#000",
          whiteSpace: "nowrap",
          flexShrink: 0
        },
        children: /* @__PURE__ */ jsx(ContentMetadataText, { text: lessonName })
      }
    )
  ] });
}
function ModuleSummaryPage({
  moduleIndex,
  chapterNumber,
  moduleName,
  colorIndex,
  lessons,
  pageWidth,
  pageHeight,
  bleedPx = 0
}) {
  const ci = colorIndex ?? Math.min(moduleIndex, 2);
  const tableSvg = MODULE_SUMMARY_TABLE_SVGS[ci] || MODULE_SUMMARY_TABLE_SVGS[0];
  const accentColor = TOC_MODULE_COLORS[ci] || TOC_MODULE_COLORS[0];
  const totalW = pageWidth + bleedPx * 2;
  const totalH = pageHeight + bleedPx * 2;
  const trimCenterX = bleedPx + pageWidth / 2;
  const trimCenterY = bleedPx + pageHeight / 2;
  const svgLeft = bleedPx + (pageWidth - SVG_W) / 2;
  const svgTop = bleedPx + (pageHeight - SVG_H) / 2;
  const pageStyle = {
    position: "relative",
    overflow: "hidden",
    boxSizing: "border-box",
    width: totalW,
    height: totalH,
    background: accentColor
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs("div", { className: "print-page", "data-testid": "module-summary-page-1", style: pageStyle, children: [
      /* @__PURE__ */ jsx(
        "div",
        {
          "data-testid": "module-summary-bubble",
          style: {
            position: "absolute",
            left: trimCenterX,
            top: trimCenterY - 169,
            transform: "translate(-50%, -50%)",
            width: 374,
            height: 247
          },
          children: /* @__PURE__ */ jsx(ChapterBubble, { number: chapterNumber ?? moduleIndex + 1, accentColor })
        }
      ),
      /* @__PURE__ */ jsx(
        "p",
        {
          "data-testid": "module-summary-title",
          className: "font-dunggeunmiso",
          style: {
            position: "absolute",
            left: trimCenterX,
            top: trimCenterY + 35,
            transform: "translateX(-50%)",
            width: 650,
            height: 188,
            margin: 0,
            fontSize: 72,
            fontWeight: 700,
            color: "#fff",
            textAlign: "center",
            lineHeight: 1.3,
            letterSpacing: "3.6px"
          },
          children: /* @__PURE__ */ jsx(ContentMetadataText, { text: moduleName })
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "print-page", "data-testid": "module-summary-page-2", style: pageStyle, children: [
      /* @__PURE__ */ jsx(
        "img",
        {
          "data-testid": "module-summary-table",
          src: tableSvg,
          alt: "",
          style: {
            position: "absolute",
            left: svgLeft,
            top: svgTop,
            width: SVG_W,
            height: SVG_H,
            pointerEvents: "none"
          }
        }
      ),
      /* @__PURE__ */ jsx(
        "div",
        {
          "data-testid": "module-summary-lesson-list",
          style: {
            position: "absolute",
            left: trimCenterX - LESSON_LIST_MIN_WIDTH / 2,
            top: trimCenterY,
            transform: "translateY(-50%)",
            width: "max-content",
            minWidth: LESSON_LIST_MIN_WIDTH,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: 10
          },
          children: lessons.map((lesson, i) => /* @__PURE__ */ jsxs(
            "div",
            {
              style: {
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 10
              },
              children: [
                /* @__PURE__ */ jsx(
                  LessonListItem,
                  {
                    number: i + 1,
                    lessonName: lesson.lessonName,
                    accentColor
                  }
                ),
                i < lessons.length - 1 && /* @__PURE__ */ jsx(
                  "div",
                  {
                    style: {
                      width: "100%",
                      height: 4
                    },
                    children: /* @__PURE__ */ jsx(PrintDotLine, {})
                  }
                )
              ]
            },
            i
          ))
        }
      )
    ] })
  ] });
}
export {
  ModuleSummaryPage
};
//# sourceMappingURL=ModuleSummaryPage.js.map
